"use client";

import { motion } from "framer-motion";
import { ArrowRightIcon, type LucideIcon } from "lucide-react";
import {
  BadgeCheckIcon,
  GlobeIcon,
  HandshakeIcon,
  PlugIcon,
  TerminalIcon,
  TrendingUpIcon,
  ZapIcon,
} from "lucide-react";
import Link from "next/link";

import {
  aiCustomerLogos,
  CustomerLogos,
  customerLogos,
} from "@/app/(marketing)/_components/customer-logos";
import { BOOK_A_CALL_HREF } from "@/app/(marketing)/_components/nav-links";
import Testimonials from "@/app/(marketing)/_components/testimonial";
import { Container } from "@/app/(marketing)/_components/ui/container";

import PartnersFAQ from "./partners-faq";

// Inner (client) content only. The server `page.tsx` wraps this in
// SubpageLayout; importing that layout here would pull its server-only chain
// (announcement-bar -> blog-posts -> `fs`) into the client bundle and break the
// Turbopack build.
export default function PartnersLandingPage() {
  return (
    <>
      <PartnerBenefits />
      <Testimonials />
      <DeliverForCustomers />
      <WaysToPartner />
      <PartnersFAQ />
    </>
  );
}

export function PartnersHero() {
  return (
    <section className="relative pt-20 pb-32 md:pt-28 md:pb-44">
      <Container>
        <div className="relative z-10 mx-auto max-w-[820px] text-center">
          <div className="mb-6 flex justify-center">
            <span className="inline-flex items-center rounded-full border-[0.8px] border-white/20 px-4 py-1 text-sm tracking-wide text-white/80 uppercase">
              Partner with Terac
            </span>
          </div>
          <h1 className="text-heading-40 sm:text-heading-56 md:text-heading-64 mb-6 text-balance text-white">
            Grow faster.
            <br className="hidden sm:block" />{" "}
            <span className="sm:whitespace-nowrap">Partner with Terac.</span>
          </h1>

          <p className="mx-auto mb-12 max-w-[640px] text-lg text-white/80">
            Integrate verified experts into your platform, or partner with us on
            go-to-market, to deliver better research and AI outcomes for your
            customers.
          </p>

          <div className="flex flex-col items-center justify-center gap-4 sm:flex-row">
            <Link
              href={BOOK_A_CALL_HREF}
              className="text-marketing-blue inline-flex items-center gap-2 rounded-full bg-white px-6 py-3 text-sm font-medium transition-colors hover:bg-white/90"
            >
              Become a partner
              <ArrowRightIcon className="h-4 w-4" />
            </Link>
            <Link
              href="/contact?inquiry=partnership"
              className="group flex items-center gap-2 text-sm font-medium text-white/90 transition-colors hover:text-white"
            >
              Contact sales
              <ArrowRightIcon className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
            </Link>
          </div>
        </div>

        <div className="mt-20 lg:mt-32">
          <CustomerLogos
            label="Trusted by leading research and AI platforms"
            entries={[...customerLogos, ...aiCustomerLogos]}
          />
        </div>
      </Container>
    </section>
  );
}

interface Benefit {
  icon: LucideIcon;
  title: string;
  description: string;
  proof: string;
}

const benefits: Benefit[] = [
  {
    icon: BadgeCheckIcon,
    title: "Boost your product and brand",
    description:
      "Put identity-verified experts inside your own product. Terac's vetted supply makes your platform the place serious research and AI work gets done, without you building a panel.",
    proof: "200+ specialized domains",
  },
  {
    icon: TrendingUpIcon,
    title: "Open a new revenue stream",
    description:
      "Resell expert recruitment to your customers or earn on every project routed through Terac. Partners turn human data collection into recurring revenue.",
    proof: "Revenue share on every project",
  },
  {
    icon: PlugIcon,
    title: "Ship with one integration",
    description:
      "Embed the panel through our API and MCP. A single integration covers recruitment, screening, moderation, and verified payout, so your team ships in days.",
    proof: "One API, one integration",
  },
];

function PartnerBenefits() {
  return (
    <section className="py-20">
      <Container>
        <div className="mb-16 flex flex-col items-center gap-8">
          <div className="bg-marketing-card-blue rounded-t-[10px] px-2.5 py-1.5">
            <p className="text-marketing-blue font-mono text-sm tracking-[-0.28px]">
              Why partner with Terac
            </p>
          </div>
          <h2 className="text-marketing-text text-heading-32 md:text-heading-40 lg:text-heading-48 max-w-[700px] text-center text-balance">
            The supply layer for verified human labor
          </h2>
        </div>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
          {benefits.map((benefit, index) => (
            <motion.div
              key={benefit.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: index * 0.1 }}
              className="border-marketing-border bg-marketing-cream-light flex flex-col rounded-[20px] border p-8"
            >
              <div className="bg-marketing-card-blue mb-4 flex h-12 w-12 items-center justify-center rounded-full">
                <benefit.icon className="text-marketing-blue h-6 w-6" />
              </div>
              <h3 className="text-marketing-text text-heading-20 mb-3 font-medium text-balance">
                {benefit.title}
              </h3>
              <p className="text-marketing-text-muted mb-6 flex-1 text-base">
                {benefit.description}
              </p>
              <div className="flex items-center gap-2.5">
                <div className="bg-marketing-blue h-1.5 w-1.5 shrink-0 rounded-full" />
                <p className="text-marketing-text text-sm font-medium">
                  {benefit.proof}
                </p>
              </div>
            </motion.div>
          ))}
        </div>
      </Container>
    </section>
  );
}

interface ValueProp {
  icon: LucideIcon;
  label: string;
  title: string;
  description: string;
  proof: string;
}

const valueProps: ValueProp[] = [
  {
    icon: BadgeCheckIcon,
    label: "Quality",
    title: "Deliver high-quality data, every time",
    description:
      "Forget panels that promise millions but deliver unusable data after weeks. Terac supplies identity-verified experts, screened by an AI moderator that catches fraud and effort-faking before results ever reach your customers.",
    proof: "Pay only on verified completion",
  },
  {
    icon: GlobeIcon,
    label: "Reach",
    title: "Supply the expertise your customers need",
    description:
      "From nationally representative samples to niche domain experts for frontier AI, reach the exact humans each project needs across 195 countries and 200+ specialized domains.",
    proof: "200+ domains, 195 countries",
  },
  {
    icon: ZapIcon,
    label: "Speed",
    title: "Deliver results in hours, not weeks",
    description:
      "Launch in the morning and return verified data by the afternoon. Recruitment, screening, and moderation run automatically, so your customers never wait on supply.",
    proof: "Median launch-to-result under 24h",
  },
];

function DeliverForCustomers() {
  return (
    <section className="py-20">
      <Container>
        <div className="mb-16 flex flex-col items-center gap-8">
          <div className="bg-marketing-card-blue rounded-t-[10px] px-2.5 py-1.5">
            <p className="text-marketing-blue font-mono text-sm tracking-[-0.28px]">
              What your customers get
            </p>
          </div>
          <h2 className="text-marketing-text text-heading-32 md:text-heading-40 lg:text-heading-48 max-w-[700px] text-center text-balance">
            Better outcomes for the people you serve
          </h2>
        </div>

        <div className="border-marketing-border bg-marketing-cream-light overflow-hidden rounded-[20px] border">
          {valueProps.map((prop, index) => (
            <motion.div
              key={prop.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: index * 0.1 }}
              className="border-marketing-border grid grid-cols-1 gap-6 border-b px-6 py-10 last:border-b-0 md:grid-cols-12 md:px-12 md:py-12 lg:px-16"
            >
              <div className="md:col-span-5">
                <div className="mb-4 flex items-center gap-3">
                  <div className="bg-marketing-card-blue flex h-10 w-10 items-center justify-center rounded-full">
                    <prop.icon className="text-marketing-blue h-5 w-5" />
                  </div>
                  <p className="text-marketing-blue font-mono text-sm tracking-[-0.28px]">
                    {prop.label}
                  </p>
                </div>
                <h3 className="text-marketing-text text-heading-24 md:text-heading-32 text-balance">
                  {prop.title}
                </h3>
              </div>
              <div className="md:col-span-7 md:pl-8">
                <p className="text-marketing-text-muted text-base md:text-lg">
                  {prop.description}
                </p>
                <div className="border-marketing-border mt-6 flex w-fit items-center gap-2.5 rounded-full border bg-white px-4 py-2">
                  <div className="bg-marketing-blue h-1.5 w-1.5 shrink-0 rounded-full" />
                  <p className="text-marketing-text text-sm font-medium">
                    {prop.proof}
                  </p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </Container>
    </section>
  );
}

interface PartnerModel {
  icon: LucideIcon;
  label: string;
  title: string;
  description: string;
  cta: { label: string; href: string };
}

const partnerModels: PartnerModel[] = [
  {
    icon: PlugIcon,
    label: "Integration partner",
    title: "Embed the panel",
    description:
      "Bring Terac's verified experts into your research or AI product through our API and MCP. We handle recruitment, screening, and payout; you keep the customer relationship.",
    cta: { label: "Book a demo", href: BOOK_A_CALL_HREF },
  },
  {
    icon: HandshakeIcon,
    label: "Go-to-market partner",
    title: "Co-sell and refer",
    description:
      "Partner with our team to bring Terac to your customers, or refer teams that need verified human data and earn on every project they run.",
    cta: { label: "Contact sales", href: "/contact?inquiry=partnership" },
  },
  {
    icon: TerminalIcon,
    label: "Developer partner",
    title: "Build on the MCP",
    description:
      "Use the expert labor MCP to let your agents source, screen, and pay verified humans on demand. The programmatic interface for human work.",
    cta: { label: "Talk to our team", href: BOOK_A_CALL_HREF },
  },
];

function WaysToPartner() {
  return (
    <section className="py-20">
      <Container>
        <div className="mb-16 flex flex-col items-center gap-8">
          <div className="bg-marketing-card-blue rounded-t-[10px] px-2.5 py-1.5">
            <p className="text-marketing-blue font-mono text-sm tracking-[-0.28px]">
              Ways to partner
            </p>
          </div>
          <h2 className="text-marketing-text text-heading-32 md:text-heading-40 lg:text-heading-48 max-w-[700px] text-center text-balance">
            Three ways to grow with Terac
          </h2>
        </div>

        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
          {partnerModels.map((model, index) => (
            <motion.div
              key={model.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: index * 0.1 }}
              className="border-marketing-border bg-marketing-cream-light flex flex-col rounded-[20px] border p-8"
            >
              <div className="bg-marketing-card-blue mb-4 flex h-12 w-12 items-center justify-center rounded-full">
                <model.icon className="text-marketing-blue h-6 w-6" />
              </div>
              <p className="text-marketing-blue mb-2 font-mono text-sm tracking-[-0.28px]">
                {model.label}
              </p>
              <h3 className="text-marketing-text text-heading-20 mb-3 font-medium text-balance">
                {model.title}
              </h3>
              <p className="text-marketing-text-muted mb-8 flex-1 text-base">
                {model.description}
              </p>
              <div className="flex">
                <Link
                  href={model.cta.href}
                  className="text-marketing-blue inline-flex items-center gap-2 text-sm font-medium"
                >
                  {model.cta.label}
                  <ArrowRightIcon className="h-4 w-4" />
                </Link>
              </div>
            </motion.div>
          ))}
        </div>
      </Container>
    </section>
  );
}
